import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UploadSimple, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const CITIES = ["Mumbai", "Delhi NCR", "Bengaluru", "Hyderabad", "Pune", "Chennai", "Kolkata", "Other"];
const TENURES = ["1 – 2 Years", "3 – 5 Years", "5+ Years"];

export default function IntentForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", city: "Mumbai", ticket_amount: "", tenure: "3 – 5 Years", pan: false, aadhaar: false, notes: "" });

  useEffect(() => {
    api.get(`/brands/${slug}`).then(({ data }) => {
      setBrand(data);
      setForm((f) => ({ ...f, full_name: user?.name || "", email: user?.email || "", phone: user?.phone || "", ticket_amount: data.ticket_min }));
    }).catch(() => toast.error("Brand not found"));
  }, [slug, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-navy-950 placeholder:text-gray-400 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-colors";
  const labelCls = "text-xs font-medium text-gray-600 mb-1.5 block";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/intents", {
        brand_slug: slug, full_name: form.full_name, email: form.email, phone: form.phone,
        ticket_amount: Number(form.ticket_amount), intent_fee: 5000, brand_mix: form.tenure, notes: form.notes,
      });
      toast.success("Intent submitted. Proceed to payment.");
      navigate(`/pay/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  if (!brand) return <div className="px-6 py-32 text-center text-ebg-muted font-data">Loading…</div>;

  return (
    <div data-testid="intent-form-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <p className="eyebrow mb-2">Investment Intent Form</p>
      <h1 className="font-display text-4xl md:text-5xl text-white">Tell us about your investment interest.</h1>
      <p className="text-ebg-muted mt-2">For <span className="text-gold">{brand.name}</span></p>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-8 shadow-2xl space-y-8">
          {/* personal */}
          <div>
            <h2 className="font-display text-2xl text-navy-950 mb-4">Personal Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Full Name</label><input data-testid="intent-name-input" required value={form.full_name} onChange={set("full_name")} className={inputCls} /></div>
              <div><label className={labelCls}>Email Address</label><input data-testid="intent-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} /></div>
              <div><label className={labelCls}>Mobile Number</label><input data-testid="intent-phone-input" required value={form.phone} onChange={set("phone")} className={inputCls} /></div>
              <div><label className={labelCls}>City</label>
                <select value={form.city} onChange={set("city")} data-testid="intent-city" className={inputCls}>{CITIES.map((c) => <option key={c}>{c}</option>)}</select>
              </div>
            </div>
          </div>
          {/* preferences */}
          <div>
            <h2 className="font-display text-2xl text-navy-950 mb-4">Investment Preferences</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Brand Selected</label><input value={brand.name} disabled className={`${inputCls} opacity-70`} /></div>
              <div><label className={labelCls}>Preferred Investment Amount (₹)</label>
                <input data-testid="intent-ticket-input" type="number" required min={brand.ticket_min} value={form.ticket_amount} onChange={set("ticket_amount")} className={inputCls} />
                <p className="text-[0.7rem] text-gray-400 mt-1">Range {formatINR(brand.ticket_min)} – {formatINR(brand.ticket_max)}</p>
              </div>
              <div className="md:col-span-2"><label className={labelCls}>Investment Tenure</label>
                <select value={form.tenure} onChange={set("tenure")} data-testid="intent-tenure" className={inputCls}>{TENURES.map((t) => <option key={t}>{t}</option>)}</select>
              </div>
            </div>
          </div>
          {/* documents */}
          <div>
            <h2 className="font-display text-2xl text-navy-950 mb-4">Upload Documents <span className="text-xs font-normal text-gray-400">(optional, demo)</span></h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[["pan", "PAN Card"], ["aadhaar", "Aadhaar Card"]].map(([k, l]) => (
                <label key={k} className={`border border-dashed rounded-lg p-5 flex items-center gap-3 cursor-pointer transition-colors ${form[k] ? "border-gold bg-gold/5" : "border-gray-300 hover:border-gold"}`}>
                  <input type="file" className="hidden" onChange={() => setForm({ ...form, [k]: true })} data-testid={`intent-upload-${k}`} />
                  {form[k] ? <CheckCircle size={22} className="text-gold" weight="fill" /> : <UploadSimple size={22} className="text-gray-400" />}
                  <div><p className="text-sm text-navy-950 font-medium">{l}</p><p className="text-xs text-gray-400">{form[k] ? "Uploaded" : "Click to upload"}</p></div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* summary */}
        <aside className="lg:col-span-1">
          <div className="bg-navy-900 border border-gold/20 rounded-2xl p-6 lg:sticky lg:top-24">
            <h3 className="font-display text-2xl text-white mb-5">Summary</h3>
            <div className="space-y-4">
              {[["Brand Selected", brand.name], ["Investment Amount", formatINR(Number(form.ticket_amount || 0))], ["Tenure", form.tenure], ["Intent Fee", formatINR(5000)]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-sm text-ebg-muted">{k}</span><span className="text-sm text-white font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
            <button data-testid="intent-continue-btn" disabled={loading} className="w-full mt-6 btn-gold py-3.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? "Submitting…" : <>Proceed to Payment <ArrowRight size={18} /></>}
            </button>
            <p className="text-xs text-ebg-muted mt-4">You will pay a refundable intent fee in the next step.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
